import { useIssuePermissions } from '@issuemanager/client/features/issues';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { GetUserRolesResponseDto } from '@issuemanager/api-client';
import {
  APP_ERROR_TYPE,
  ERROR_USER_INTERFACE,
  useAppErrorHandler,
} from '@issuemanager/apps/client/contexts';
import { ExtendedAxiosError } from '@issuemanager/apps/client/hooks';
import { paths } from '@issuemanager/apps/client/paths';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import { UseQueryResult } from '@tanstack/react-query';

export default function useIssueAuthorization(code: string | undefined): {
  userRoles: UseQueryResult<GetUserRolesResponseDto, ExtendedAxiosError>;
  canManage: boolean | undefined;
} {
  const appErrorHandler = useAppErrorHandler();
  const isDraftIssuesEnabled = useFeatureFlags('draftIssues');
  const { pathname } = useLocation();

  const issuePermissions = useIssuePermissions(code);

  const canManage =
    (isDraftIssuesEnabled && issuePermissions.data?.isCreator) ||
    issuePermissions.data?.isIssueTypeManager ||
    issuePermissions.data?.isManager ||
    issuePermissions.data?.isOwner ||
    issuePermissions.data?.isActionOwner ||
    issuePermissions.data?.isApprover ||
    issuePermissions.data?.isActionApprover;

  useEffect(() => {
    if (issuePermissions.isLoading) return;
    if (!canManage && pathname.includes(paths.issues.manage)) {
      appErrorHandler.setError({
        type: APP_ERROR_TYPE.UNAUTHORIZED_TO_MANAGE_ISSUE,
        userInterface: ERROR_USER_INTERFACE.PAGE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuePermissions.isLoading, canManage, pathname]);

  return {
    userRoles: issuePermissions,
    canManage,
  };
}
